import React, { useState } from "react";
import { 
  Card, 
  CardHeader, 
  CardContent, 
  Typography, 
  Grid, 
  Box,
  Button,
  InputAdornment,
  IconButton,
  Tooltip,
  Divider,
  Chip,
  Stack,
  Paper,
  Radio
} from "@mui/material";
import { 
  Map as MapIcon, 
  LocationOn, 
  GpsFixed, 
  Phone, 
  Email, 
  Add, 
  DeleteOutline,
  Info,
  CheckCircle,
  HelpOutline,
  Home
} from "@mui/icons-material";
import CustomSelect from "../../components/FormFields/CustomSelect/CustomSelect";
import CustomInput from "../../components/FormFields/CustomInput";
import MapAddressPicker from "../../components/CustomForm/MapAddressPicker";

const preferredContactMethodOptions = [
  { value: "phone", label: "Phone Call" },
  { value: "email", label: "Email" },
  { value: "sms", label: "SMS / Text" },
  { value: "post", label: "Post / Letter" },
  { value: "in_person", label: "In-Person Visit" },
  { value: "whatsapp", label: "WhatsApp" }
];

const phoneTypeOptions = [
  { value: "mobile", label: "Mobile" },
  { value: "home", label: "Home" },
  { value: "work", label: "Work" },
  { value: "carer", label: "Carer" },
  { value: "family", label: "Family" },
  { value: "emergency", label: "Emergency" },
  { value: "other", label: "Other" }
];

const emailTypeOptions = [
  { value: "personal", label: "Personal" },
  { value: "work", label: "Work" },
  { value: "family", label: "Family" },
  { value: "carer", label: "Carer" },
  { value: "other", label: "Other" }
];

const preferenceOptions = [
  { value: 1, label: "1st Choice (Best)" },
  { value: 2, label: "2nd Choice" },
  { value: 3, label: "3rd Choice" },
  { value: 4, label: "4th Choice" },
  { value: 5, label: "5th Choice (Last)" }
];

const addressTypeOptions = [
  { value: "permanent", label: "Permanent Residence" },
  { value: "temporary", label: "Temporary Address" },
  { value: "family", label: "Family Member Address" },
  { value: "carer", label: "Carer Address" },
  { value: "other", label: "Other" }
];

const ContactDetails = ({ handleChange, data, setFormData, errors }) => {
  const [isMapOpen, setIsMapOpen] = useState(false);
  const [editingAddressId, setEditingAddressId] = useState(null);

  const [addresses, setAddresses] = useState(() => {
    if (data?.addresses && data.addresses.length > 0) {
      return data.addresses;
    }
    return [{ 
      id: 1, 
      address_1: "", 
      address_2: "", 
      town: "", 
      county: "", 
      postcode: "",
      lat: null,
      lng: null,
      address_type: "permanent",
      is_service_delivery: true
    }];
  });

  const [phones, setPhones] = useState(() => {
    if (data?.phones && data.phones.length > 0) {
      return data.phones;
    }
    return [{ id: 1, number: "", phone_type: "mobile", preference: 1 }];
  });

  const [emails, setEmails] = useState(() => {
    if (data?.emails && data.emails.length > 0) {
      return data.emails;
    }
    return [{ id: 1, email: "", email_type: "personal", preference: 1 }];
  });

  const handleLocalChange = (event) => {
    const { name, value } = event.target;
    if (handleChange) {
      handleChange(event);
    }
  };

  const updateFormData = (key, value) => {
    if (setFormData) {
      setFormData(key, value);
    }
  };

  const handleMapLocationSelect = (mapData) => {
    if (editingAddressId !== null) {
      const updatedAddresses = addresses.map(addr => 
        addr.id === editingAddressId 
          ? { 
              ...addr, 
              address_1: mapData.line1 || "",
              address_2: mapData.line2 || "",
              town: mapData.town || "",
              county: mapData.county || "",
              postcode: mapData.postcode || "",
              lat: mapData.lat, 
              lng: mapData.lng  
            }
          : addr
      );
      setAddresses(updatedAddresses);
      updateFormData("addresses", updatedAddresses);
      setEditingAddressId(null);
    } else {
      const newId = addresses.length > 0 ? Math.max(...addresses.map(a => a.id)) + 1 : 1;
      const newAddress = {
        id: newId,
        address_1: mapData.line1 || "",
        address_2: mapData.line2 || "",
        town: mapData.town || "",
        county: mapData.county || "",
        postcode: mapData.postcode || "",
        lat: mapData.lat,
        lng: mapData.lng,
        address_type: "other",
        is_service_delivery: addresses.length === 0
      };
      const updatedAddresses = [...addresses, newAddress];
      setAddresses(updatedAddresses);
      updateFormData("addresses", updatedAddresses);
    }
    setIsMapOpen(false);
  };

  const handleAddressChange = (id, field, value) => {
    const updatedAddresses = addresses.map(addr => 
      addr.id === id ? { ...addr, [field]: value } : addr
    );
    setAddresses(updatedAddresses);
    updateFormData("addresses", updatedAddresses);
  };

  const handleServiceDeliveryChange = (id) => {
    const updatedAddresses = addresses.map(addr => ({
      ...addr,
      is_service_delivery: addr.id === id
    }));
    setAddresses(updatedAddresses);
    updateFormData("addresses", updatedAddresses);
  };

  const addAddress = () => {
    const newId = addresses.length > 0 ? Math.max(...addresses.map(a => a.id)) + 1 : 1;
    const newAddress = { 
      id: newId, 
      address_1: "", 
      address_2: "", 
      town: "", 
      county: "", 
      postcode: "",
      lat: null,
      lng: null,
      address_type: "other",
      is_service_delivery: addresses.length === 0
    };
    const updatedAddresses = [...addresses, newAddress];
    setAddresses(updatedAddresses);
    updateFormData("addresses", updatedAddresses);
  };

  const removeAddress = (id) => {
    if (addresses.length <= 1) return;
    const updatedAddresses = addresses.filter(addr => addr.id !== id);
    const removed = addresses.find(addr => addr.id === id);
    if (removed?.is_service_delivery && updatedAddresses.length > 0) {
      updatedAddresses[0].is_service_delivery = true;
    }
    setAddresses(updatedAddresses);
    updateFormData("addresses", updatedAddresses);
  };

  const openMapForAddress = (id) => {
    setEditingAddressId(id);
    setIsMapOpen(true);
  };

  const handlePhoneChange = (id, field, value) => {
    const updatedPhones = phones.map(phone => 
      phone.id === id ? { ...phone, [field]: value } : phone
    );
    setPhones(updatedPhones);
    updateFormData("phones", updatedPhones);
  };

  const addPhone = () => {
    const newId = phones.length > 0 ? Math.max(...phones.map(p => p.id)) + 1 : 1;
    const newPhone = { 
      id: newId, 
      number: "", 
      phone_type: "mobile", 
      preference: phones.length + 1 
    };
    const updatedPhones = [...phones, newPhone];
    setPhones(updatedPhones);
    updateFormData("phones", updatedPhones);
  };

  const removePhone = (id) => {
    if (phones.length <= 1) return;
    const updatedPhones = phones.filter(phone => phone.id !== id);
    setPhones(updatedPhones);
    updateFormData("phones", updatedPhones);
  };

  const handleEmailChange = (id, field, value) => {
    const updatedEmails = emails.map(email => 
      email.id === id ? { ...email, [field]: value } : email
    );
    setEmails(updatedEmails);
    updateFormData("emails", updatedEmails);
  };

  const addEmail = () => {
    const newId = emails.length > 0 ? Math.max(...emails.map(e => e.id)) + 1 : 1;
    const newEmail = { 
      id: newId, 
      email: "", 
      email_type: "personal", 
      preference: emails.length + 1 
    };
    const updatedEmails = [...emails, newEmail];
    setEmails(updatedEmails);
    updateFormData("emails", updatedEmails);
  };

  const removeEmail = (id) => {
    if (emails.length <= 1) return;
    const updatedEmails = emails.filter(email => email.id !== id);
    setEmails(updatedEmails);
    updateFormData("emails", updatedEmails);
  };

  const getPrimaryPhone = () => {
    const primary = phones.find(p => p.preference === 1);
    return primary?.number || "Not set";
  };

  const getPrimaryEmail = () => {
    const primary = emails.find(e => e.preference === 1);
    return primary?.email || "Not set";
  };

  const getServiceDeliveryAddress = () => {
    const serviceAddr = addresses.find(a => a.is_service_delivery);
    return serviceAddr || addresses[0];
  };

  return (
    <Box sx={{ width: '100%', mb: 1 }}>
      <MapAddressPicker 
        open={isMapOpen} 
        onClose={() => {
          setIsMapOpen(false);
          setEditingAddressId(null);
        }} 
        onLocationSelect={handleMapLocationSelect}
        initialData={editingAddressId !== null ? addresses.find(a => a.id === editingAddressId) : null}
      />

      <Card variant="outlined" sx={{ borderRadius: 2, border: '1px solid #E4E7EC' }}>
        <CardHeader
          title={
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1570EF' }}>
              Contact Details
            </Typography>
          }
          sx={{ 
            backgroundColor: '#F9FAFB', 
            py: 0.75, 
            px: 2, 
            borderBottom: '1px solid #EAECF0' 
          }}
        />
        
        <CardContent sx={{ p: 2 }}>
          
          <Paper 
            elevation={0} 
            sx={{ 
              p: 1.5, 
              mb: 2, 
              bgcolor: '#EFF8FF', 
              borderRadius: 1.5,
              border: '1px solid #B2DDFF'
            }}
          >
            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={12} md={3.5}>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Info sx={{ color: '#1570EF', fontSize: 18 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1570EF' }}>
                    Preferred Contact Method
                  </Typography>
                  <Tooltip title="How would the client prefer to be contacted?">
                    <HelpOutline sx={{ fontSize: 16, color: '#667085', cursor: 'help' }} />
                  </Tooltip>
                </Stack>
              </Grid>
              <Grid item xs={12} md={3}>
                <CustomSelect
                  name="preferred_contact_method"
                  options={preferredContactMethodOptions}
                  placeholder="Select Method"
                  value={data?.preferred_contact_method || ""}
                  onChange={handleLocalChange}
                  fullWidth
                  size="small"
                  error={!!errors?.preferred_contact_method}
                  helperText={errors?.preferred_contact_method || ""}
                />
              </Grid>
              <Grid item xs={12} md={5.5}>
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                  <Chip 
                    label={`Primary Phone: ${getPrimaryPhone()}`} 
                    size="small" 
                    variant="outlined"
                    sx={{ bgcolor: 'white', height: 24, '& .MuiChip-label': { fontSize: '0.7rem', px: 1 } }}
                  />
                  <Chip 
                    label={`Primary Email: ${getPrimaryEmail()}`} 
                    size="small" 
                    variant="outlined"
                    sx={{ bgcolor: 'white', height: 24, '& .MuiChip-label': { fontSize: '0.7rem', px: 1 } }}
                  />
                  <Chip 
                    label={`Service Address: ${getServiceDeliveryAddress()?.postcode || 'Not set'}`} 
                    size="small" 
                    variant="outlined"
                    icon={<Home sx={{ fontSize: 12 }} />}
                    sx={{ bgcolor: '#E8F5E9', height: 24, '& .MuiChip-label': { fontSize: '0.7rem', px: 1 } }}
                  />
                </Stack>
              </Grid>
            </Grid>
          </Paper>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#344054', fontSize: '0.8rem' }}>
              Addresses
            </Typography>
            <Button 
              size="small" 
              startIcon={<Add />} 
              onClick={addAddress}
              sx={{ 
                textTransform: 'none', 
                color: '#1570EF', 
                fontSize: '0.7rem', 
                py: 0.25,
                minHeight: 28
              }}
            >
              Add Address
            </Button>
          </Box>

          {addresses.map((address, index) => (
            <Paper 
              key={address.id} 
              elevation={0} 
              sx={{ 
                p: 1.5, 
                mb: 1, 
                bgcolor: address.is_service_delivery ? '#F0FDF4' : '#F9FAFB',
                borderRadius: 1.5,
                border: address.is_service_delivery ? '2px solid #86EFAC' : '1px solid #EAECF0'
              }}
            >
              <Grid container spacing={1.5} alignItems="center">
                <Grid item xs={12} sm={2.5}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Radio
                      checked={address.is_service_delivery}
                      onChange={() => handleServiceDeliveryChange(address.id)}
                      size="small"
                      sx={{ color: '#86EFAC', '&.Mui-checked': { color: '#22C55E' } }}
                    />
                    <Typography variant="caption" sx={{ fontWeight: 500, color: '#344054' }}>
                      Service Delivery
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={2.5}>
                  <CustomInput
                    label="Address Line 1"
                    value={address.address_1}
                    onChange={(e) => handleAddressChange(address.id, 'address_1', e.target.value)}
                    fullWidth
                    size="small"
                    placeholder="e.g., 7 Hutton Road"
                  />
                </Grid>

                <Grid item xs={12} sm={2}>
                  <CustomInput
                    label="Town / City"
                    value={address.town}
                    onChange={(e) => handleAddressChange(address.id, 'town', e.target.value)}
                    fullWidth
                    size="small"
                    placeholder="e.g., Birmingham"
                  />
                </Grid>

                <Grid item xs={6} sm={1.5}>
                  <CustomInput
                    label="County"
                    value={address.county}
                    onChange={(e) => handleAddressChange(address.id, 'county', e.target.value)}
                    fullWidth
                    size="small"
                    placeholder="e.g., West Midlands"
                  />
                </Grid>

                <Grid item xs={6} sm={1.5}>
                  <CustomInput
                    label="Postcode"
                    value={address.postcode}
                    onChange={(e) => handleAddressChange(address.id, 'postcode', e.target.value)}
                    fullWidth
                    size="small"
                    placeholder="e.g., B20 3RB"
                  />
                </Grid>

                <Grid item xs={12} sm={1.5}>
                  <CustomSelect
                    name={`address_type_${address.id}`}
                    options={addressTypeOptions}
                    placeholder="Type"
                    value={address.address_type || "other"}
                    onChange={(e) => handleAddressChange(address.id, 'address_type', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sm={1}>
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                    <Tooltip title="Search & Pin on Map">
                      <IconButton 
                        size="small" 
                        onClick={() => openMapForAddress(address.id)}
                        sx={{ color: '#1570EF', p: 0.5 }}
                      >
                        <MapIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {address.lat && (
                      <Tooltip title="Location pinned">
                        <CheckCircle sx={{ fontSize: 14, color: '#22C55E' }} />
                      </Tooltip>
                    )}
                    {address.is_service_delivery && (
                      <Chip 
                        label="Service" 
                        size="small" 
                        color="success" 
                        sx={{ 
                          borderRadius: 1, 
                          height: 18, 
                          '& .MuiChip-label': { fontSize: '0.55rem', px: 0.5 } 
                        }}
                      />
                    )}
                    {addresses.length > 1 && (
                      <IconButton 
                        size="small" 
                        onClick={() => removeAddress(address.id)}
                        sx={{ color: '#D92D20', p: 0.5 }}
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </Paper>
          ))}

          <Divider sx={{ my: 1.5, borderStyle: 'dashed' }} />

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#344054', fontSize: '0.8rem' }}>
              Phone Numbers
            </Typography>
            <Button 
              size="small" 
              startIcon={<Add />} 
              onClick={addPhone}
              sx={{ 
                textTransform: 'none', 
                color: '#1570EF', 
                fontSize: '0.7rem', 
                py: 0.25,
                minHeight: 28
              }}
            >
              Add Phone Number
            </Button>
          </Box>

          {phones.map((phone, index) => (
            <Paper 
              key={phone.id} 
              elevation={0} 
              sx={{ 
                p: 1.5, 
                mb: 1, 
                bgcolor: phone.preference === 1 ? '#F0FDF4' : '#F9FAFB',
                borderRadius: 1.5,
                border: phone.preference === 1 ? '1px solid #86EFAC' : '1px solid #EAECF0'
              }}
            >
              <Grid container spacing={1.5} alignItems="center">
                <Grid item xs={12} sm={3.5}>
                  <CustomInput
                    label={`Phone ${index + 1}`}
                    value={phone.number}
                    onChange={(e) => handlePhoneChange(phone.id, 'number', e.target.value)}
                    fullWidth
                    size="small"
                    placeholder="e.g., 07774242232"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Phone sx={{ fontSize: 16, color: '#667085' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                
                <Grid item xs={6} sm={2.5}>
                  <CustomSelect
                    name={`phone_type_${phone.id}`}
                    options={phoneTypeOptions}
                    placeholder="Select Phone Type"
                    value={phone.phone_type}
                    onChange={(e) => handlePhoneChange(phone.id, 'phone_type', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                
                <Grid item xs={4} sm={2.5}>
                  <CustomSelect
                    name={`phone_pref_${phone.id}`}
                    options={preferenceOptions}
                    placeholder="Select Preference"
                    value={phone.preference}
                    onChange={(e) => handlePhoneChange(phone.id, 'preference', Number(e.target.value))}
                    fullWidth
                    size="small"
                  />
                </Grid>
                
                <Grid item xs={2} sm={3.5}>
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                    {phone.preference === 1 && (
                      <Chip 
                        label="Primary" 
                        size="small" 
                        color="success" 
                        icon={<CheckCircle sx={{ fontSize: 12 }} />}
                        sx={{ 
                          borderRadius: 1, 
                          fontWeight: 600, 
                          height: 22, 
                          '& .MuiChip-label': { fontSize: '0.65rem', px: 1 } 
                        }}
                      />
                    )}
                    {phones.length > 1 && (
                      <IconButton 
                        size="small" 
                        onClick={() => removePhone(phone.id)}
                        sx={{ color: '#D92D20', p: 0.5 }}
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </Paper>
          ))}

          <Divider sx={{ my: 1.5, borderStyle: 'dashed' }} />

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#344054', fontSize: '0.8rem' }}>
              Email Addresses
            </Typography>
            <Button 
              size="small" 
              startIcon={<Add />} 
              onClick={addEmail}
              sx={{ 
                textTransform: 'none', 
                color: '#1570EF', 
                fontSize: '0.7rem', 
                py: 0.25,
                minHeight: 28
              }}
            >
              Add Email Address
            </Button>
          </Box>

          {emails.map((email, index) => (
            <Paper 
              key={email.id} 
              elevation={0} 
              sx={{ 
                p: 1.5, 
                mb: 1, 
                bgcolor: email.preference === 1 ? '#F0FDF4' : '#F9FAFB',
                borderRadius: 1.5,
                border: email.preference === 1 ? '1px solid #86EFAC' : '1px solid #EAECF0'
              }}
            >
              <Grid container spacing={1.5} alignItems="center">
                <Grid item xs={12} sm={3.5}>
                  <CustomInput
                    label={`Email ${index + 1}`}
                    value={email.email}
                    onChange={(e) => handleEmailChange(email.id, 'email', e.target.value)}
                    fullWidth
                    size="small"
                    placeholder="e.g., user@example.com"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email sx={{ fontSize: 16, color: '#667085' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                
                <Grid item xs={6} sm={2.5}>
                  <CustomSelect
                    name={`email_type_${email.id}`}
                    options={emailTypeOptions}
                    placeholder="Select Email Type"
                    value={email.email_type}
                    onChange={(e) => handleEmailChange(email.id, 'email_type', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                
                <Grid item xs={4} sm={2.5}>
                  <CustomSelect
                    name={`email_pref_${email.id}`}
                    options={preferenceOptions}
                    placeholder="Select Preference"
                    value={email.preference}
                    onChange={(e) => handleEmailChange(email.id, 'preference', Number(e.target.value))}
                    fullWidth
                    size="small"
                  />
                </Grid>
                
                <Grid item xs={2} sm={3.5}>
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                    {email.preference === 1 && (
                      <Chip 
                        label="Primary" 
                        size="small" 
                        color="success" 
                        icon={<CheckCircle sx={{ fontSize: 12 }} />}
                        sx={{ 
                          borderRadius: 1, 
                          fontWeight: 600, 
                          height: 22, 
                          '& .MuiChip-label': { fontSize: '0.65rem', px: 1 } 
                        }}
                      />
                    )}
                    {emails.length > 1 && (
                      <IconButton 
                        size="small" 
                        onClick={() => removeEmail(email.id)}
                        sx={{ color: '#D92D20', p: 0.5 }}
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </Paper>
          ))}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ContactDetails;